import { InfoSectionData } from './FieldInfoButton';

// Общая подсказка для всей страницы Maps & Nodes
export const mapsAndNodesInfo: InfoSectionData[] = [
  {
    title: 'Overview',
    description: `
      <strong>Maps & Nodes</strong> allows you to manage your field's playing areas and the physical devices (nodes) installed on them.
      <br/><br/>
      <strong>Main Node</strong> - Your field's internet gateway (<strong>InternetNode</strong>) that connects all other nodes to the cloud.
      <br/>
      <strong>Maps</strong> - Represent different playing zones or arenas at your field.
      <br/>
      <strong>Nodes</strong> - Physical <strong>LoRa mesh network</strong> devices that enable communication and game tracking across your field.
    `,
  },
  {
    title: 'Getting Started',
    description: `
      1. <strong>Set Main Node</strong> - Configure your <strong>OfficeNode</strong> as the main gateway (required for real-time data)<br/>
      2. <strong>Create a Map</strong> - Add your playing areas with names, descriptions, and optional images<br/>
      3. <strong>Get Nodes</strong> - Purchase physical nodes from <strong>NovaWarfare</strong> (each has a unique <strong>Node Code</strong>)<br/>
      4. <strong>Assign Nodes</strong> - Link your nodes to specific maps using their <strong>Node Code</strong><br/>
      5. <strong>Add Position</strong> - Describe where each node is placed so referees can find them
    `,
  },
  {
    title: 'Main Node (Office Gateway)',
    description: `
      The <strong>Main Node</strong> is a special <strong>OfficeNode</strong> that acts as your field's primary gateway to the <strong>NovaWarfare cloud</strong>.
      <br/><br/>
      • It should be placed in your <strong>office or reception area</strong> near a WiFi router<br/>
      • It connects all map nodes to the internet for <strong>real-time game tracking</strong><br/>
      • Only <strong>one Main Node per field</strong> is supported<br/>
      • The Main Node <strong>cannot be assigned to any map</strong> - it operates independently<br/>
      • You need the <strong>Node Code</strong> from your purchased OfficeNode device to set it up
    `,
  },
  {
    title: 'Creating Maps',
    fields: [
      {
        name: 'Map Name',
        description: 'A clear name for your playing area (e.g., <strong>"Forest Arena"</strong>, <strong>"Urban Zone"</strong>, <strong>"Speedball Field"</strong>).',
      },
      {
        name: 'Description',
        description: 'Optional details about the map - <strong>terrain type</strong>, <strong>size</strong>, special features, or any notes for players and staff.',
      },
      {
        name: 'Map Image',
        description: 'Upload a <strong>photo or diagram</strong> of your playing area. This helps <strong>referees</strong> and <strong>players</strong> understand the layout.',
      },
    ],
  },
  {
    title: 'Managing Nodes',
    fields: [
      {
        name: 'Node Code',
        description: 'The unique identifier printed on your physical device or included in your purchase documentation. Enter this to find and assign your node.',
      },
      {
        name: 'Position Description',
        description: 'A text description of where the node is <strong>physically located</strong> on the map. Be specific so referees can easily find it.<br/><br/>' +
          '<strong>Examples:</strong><br/>' +
          '• "North entrance, behind the large oak tree"<br/>' +
          '• "Building A, second floor, left window"<br/>' +
          '• "Center of the speedball field, bunker #3"',
      },
      {
        name: 'Node Types',
        description: '<strong>InternetNode</strong> - Main gateway that connects to the internet. This type can <strong>only be used as the Main Node</strong> (Office Gateway) and cannot be assigned to any map.<br/>' +
          '<strong>MapNode</strong> - Standard game device for objectives like checkpoints, control points, etc.',
      },
    ],
  },
  {
    title: 'Important Notes',
    description: `
      • <strong>Removing a node</strong> from a map makes it available for reassignment to any field<br/>
      • Nodes can only be assigned to <strong>one map at a time</strong><br/>
      • If a node is already assigned to another map, you can <strong>reassign</strong> it<br/>
      • <strong>Position descriptions</strong> help referees set up games quickly and correctly
    `,
  },
];

// Подсказка только для секции Field Maps
export const fieldMapsInfo: InfoSectionData[] = [
  {
    title: 'Overview',
    description: `
      <strong>Field Maps</strong> represent different playing zones or arenas at your field.
      <br/><br/>
      Each map can have multiple <strong>nodes</strong> assigned to it for game tracking. Only <strong>MapNode</strong> type can be assigned to maps.
    `,
  },
  {
    title: 'Creating Maps',
    fields: [
      {
        name: 'Map Name',
        description: 'A clear name for your playing area (e.g., <strong>"Forest Arena"</strong>, <strong>"Urban Zone"</strong>, <strong>"Speedball Field"</strong>).',
      },
      {
        name: 'Description',
        description: 'Optional details about the map - terrain type, size, special features, or any notes for players and staff.',
      },
      {
        name: 'Map Image',
        description: 'Upload a photo or diagram of your playing area. This helps <strong>referees</strong> and <strong>players</strong> understand the layout.',
      },
    ],
  },
  {
    title: 'Managing Nodes on Maps',
    fields: [
      {
        name: 'Node Code',
        description: 'The unique identifier printed on your physical device or included in your purchase documentation. Enter this to find and assign your node.',
      },
      {
        name: 'Position Description',
        description: 'A text description of where the node is <strong>physically located</strong> on the map. Be specific so referees can easily find it.<br/><br/>' +
          '<strong>Examples:</strong><br/>' +
          '• "North entrance, behind the large oak tree"<br/>' +
          '• "Building A, second floor, left window"<br/>' +
          '• "Center of the speedball field, bunker #3"',
      },
    ],
  },
  {
    title: 'Important Notes',
    description: `
      • <strong>Removing a node</strong> from a map makes it available for reassignment<br/>
      • Nodes can only be assigned to <strong>one map at a time</strong><br/>
      • If a node is already assigned to another map, you can <strong>reassign</strong> it<br/>
      • <strong>InternetNode</strong> cannot be assigned to maps - use it as Main Node instead
    `,
  },
  {
    title: 'Map Actions',
    fields: [
      {
        name: 'Edit',
        description: 'Update map name, description, images, and YouTube videos.',
      },
      {
        name: 'Nodes',
        description: 'Assign or remove physical LoRa nodes to this map using their Node Codes.',
      },
      {
        name: 'Schema Editor',
        description: 'Open the visual map editor to draw your field layout, place buildings, barriers, zones, and position nodes on an interactive canvas. You can also create <strong>Mission Templates</strong> with pre-configured device positions for quick game setup.',
      },
      {
        name: 'Delete',
        description: 'Permanently remove the map. This will also unassign all nodes from this map.',
      },
    ],
  },
  {
    title: 'Map Editor - Schema Mode',
    description: `
      In <strong>Schema</strong> mode you can draw your field layout using various tools:
      <br/><br/>
      <strong>Drawing Tools:</strong><br/>
      • <strong>Rectangle</strong> - draw buildings, barriers, bunkers<br/>
      • <strong>Circle</strong> - draw circular zones or obstacles<br/>
      • <strong>Line</strong> - draw paths, boundaries, dividers<br/>
      • <strong>Text</strong> - add labels and zone names<br/>
      • <strong>Image</strong> - insert custom images or icons<br/>
      <br/>
      <strong>Features:</strong><br/>
      • Change canvas background (dark/white)<br/>
      • Select, move, resize, and rotate elements<br/>
      • Copy/paste elements<br/>
      • Layer ordering (bring to front/send to back)<br/>
      • Manual save button (don't forget to save your changes!)<br/>
      • Export map as PNG image
    `,
  },
  {
    title: 'Map Editor - Nodes Mode',
    description: `
      In <strong>Nodes</strong> mode you can position your physical nodes on the map canvas:
      <br/><br/>
      • Drag nodes from the panel to place them on the canvas<br/>
      • Move nodes to show their actual physical location<br/>
      • <strong>Lock</strong> nodes to prevent accidental movement<br/>
      • Click <strong>Save</strong> button to save node positions<br/>
      • Use position descriptions in the Nodes table for detailed location info
    `,
  },
  {
    title: 'Map Editor - Mission Templates',
    description: `
      In <strong>Mission Templates</strong> mode you can create pre-configured device layouts for quick game setup:
      <br/><br/>
      <strong>What are Mission Templates?</strong><br/>
      Mission Templates save device positions for specific operations. When starting a game, referees can load a template to see exactly where each device should be placed.
      <br/><br/>
      <strong>Creating a Template:</strong><br/>
      1. Select an operation from the dropdown<br/>
      2. Drag devices from the panel onto the canvas<br/>
      3. Position devices where they should be placed during the game<br/>
      4. Click <strong>Save Positions</strong> to save the template<br/>
      <br/>
      <strong>Features:</strong><br/>
      • Each map can have multiple templates for the same operation<br/>
      • Devices show their type on the canvas (bomb, egg, computer, etc.)<br/>
      • Templates are saved separately from the map schema<br/>
      • Load existing templates to edit device positions
    `,
  },
];
